import { css } from 'emotion';
import { observer } from 'mobx-react';
import React, { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '../../../../components/Button';
import { ButtonGroup } from '../../../../components/ButtonGroup';
import { Caption } from '../../../../components/Caption';
import { Card } from '../../../../components/Card';
import { DocumentTitle } from '../../../../components/DocumentTitle';
import { FormItem } from '../../../../components/Form';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/Toast';
import { Wrapper } from '../../../../components/Wrapper';
import { Routes } from '../../../../constants/Routes';
import { Color } from '../../../../constants/Style';
import { useContext } from '../../../../hooks/useContext';
import { Payjp3dSecureStore } from '../../store/Payjp3dSecureStore';
import { CardInputLayout } from '../components/CardInputLayout';
import { AccountDetailStore } from '../stores/AccountDetailStore';

type Callback = ComponentProps<typeof CardInputLayout>['callback'];

export const SettlementEditPage = observer(() => {
  const store = useContext(AccountDetailStore.Context);
  const { push } = useHistory();
  const { addToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const callback: Callback = useCallback(
    async input => {
      try {
        setIsProcessing(true);
        await store.updateCard({ ...input, id: input.payjpID });
        if (store.profile?.card?.id) {
          const finishParams = new URLSearchParams();
          finishParams.set('mode', 'profileUpdate');
          const returnUrl = new URLSearchParams(window.location.search).get('url');
          if (returnUrl) {
            finishParams.set('url', returnUrl);
          }
          const fullPath = `${window.location.origin}${Routes.Paths.Payjp3dSecureFinish}?${finishParams.toString()}`;
          const payjp3dSecureStore = new Payjp3dSecureStore(store.profile.card.id, fullPath, Routes.Paths.Account);
          await payjp3dSecureStore.redirect3dSecure();
        } else {
          addToast({ message: '支払方法を更新しました。', icon: 'check-circle' });
          const url = new URLSearchParams(window.location.search).get('url');
          push(url || Routes.Paths.Account);
          setIsProcessing(false);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [store, addToast, push, setIsProcessing],
  );

  const onSubmit = useRef<() => void>();

  useEffect(() => {
    const phoneCaution = store.profile?.phoneCaution || '';
    if (phoneCaution !== '') {
      addToast({ message: phoneCaution });
      push(Routes.Paths.Account);
    }
  }, [addToast, push, store.profile]);

  return (
    <Wrapper>
      <Modal visible={isProcessing || !!store.edit?.cardStatus.cardTransactionStatus?.running}>
        <Card>決済情報を更新しています...</Card>
      </Modal>
      <DocumentTitle title="決済情報編集" />
      <Caption type="header" underline align="center">
        決済情報
      </Caption>
      <CardInputLayout
        callback={callback}
        onSubmit={onSubmit}
        onStatusChange={s => store.edit?.cardStatus.setCardTransactionStatus(s)}
        onPreCardValidateChange={v => store.edit?.cardStatus.setPreCardValidate(v)}
        phone={store.profile?.formattedE164PhoneNumber}
      />
      {store.edit?.cardStatus.cardTransactionStatus?.error && (
        <div className={errorStyle}>{store.edit.cardStatus.cardTransactionStatus.error}</div>
      )}
      <FormItem>
        <ButtonGroup gutter={4}>
          <Button
            onClick={() => {
              onSubmit.current?.();
            }}
            disabled={store.edit?.cardStatus.cardTransactionStatus?.running || store.edit?.cardStatus.hasPreCardError}
          >
            この内容で保存する
          </Button>
          <Button onClick={() => push(Routes.Paths.Account)}>戻る</Button>
        </ButtonGroup>
      </FormItem>
    </Wrapper>
  );
});

const errorStyle = css`
  margin-top: 8px;
  color: ${Color.FunctionalRedNeutral};
`;
